import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import {
  LogOut, Settings, Package, ShoppingCart, FileText, Mail,
  Plus, Edit, Trash2, Save, X, Upload, Image as ImageIcon,
  Phone, Globe, Info, ShieldCheck, Lock, AtSign, FileArchive, Link, Zap, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import Editor from '@monaco-editor/react';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : 'https://mertt.tr';
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const getCurrencySymbol = (code) => {
    switch(code) {
      case 'USD': return '$'; case 'EUR': return '€'; case 'GBP': return '£';
      case 'BTC': return '₿'; case 'ETH': return 'Ξ'; default: return '₺';
    }
};

function AdminDashboard() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('settings'); 
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState('');

  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({ today_visitors: 0, total_visitors: 0, popular_pages: [] });
  
  const [qrCode, setQrCode] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  
  const [siteSettings, setSiteSettings] = useState({
    phone: '', email: '', address: '',
    instagram: '', linkedin: '', twitter: '', whatsapp: '', facebook: '',
    about_title: '', about_desc: '', vision: '', mission: '', values: '',
    footer_text: '',
    smtp_email: '', smtp_password: '', sms_api_key: '' // YENİ OTOMASYON ALANLARI
  });

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [modalType, setModalType] = useState('');

  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [emailForm, setEmailForm] = useState({ new_email: '', current_password: '' });

  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    if (!storedToken) { navigate('/mertt-komuta-merkezi-99'); return; }
    setToken(storedToken);
    verifyAuth(storedToken);
  }, []);

  useEffect(() => {
    if (token) fetchData();
  }, [token, activeTab]);

  const verifyAuth = async (authToken) => {
    try {
      await axios.get(`${API}/auth/verify`, { headers: { Authorization: `Bearer ${authToken}` } });
      setLoading(false);
    } catch (error) {
      localStorage.removeItem('admin_token');
      navigate('/mertt-komuta-merkezi-99');
    }
  };

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      switch (activeTab) {
        case 'services':
          const servicesRes = await axios.get(`${API}/services`); setServices(servicesRes.data); break;
        case 'products':
          const productsRes = await axios.get(`${API}/products`); setProducts(productsRes.data); break;
        case 'blogs':
          const blogsRes = await axios.get(`${API}/blogs`); setBlogs(blogsRes.data); break;
        case 'messages':
          const messagesRes = await axios.get(`${API}/contact`, { headers }); setMessages(messagesRes.data); break;
        case 'settings':
        case 'automation': // Automation ayarları da settings'ten çekiliyor
          const settingsRes = await axios.get(`${API}/settings`); setSiteSettings(settingsRes.data); break;
        case 'analytics': 
          const analyticsRes = await axios.get(`${API}/analytics/stats`, { headers }); setAnalyticsData(analyticsRes.data); break;
        default: break;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/mertt-komuta-merkezi-99');
  };

  const handleAdd = () => { setEditingItem(null); setModalType(activeTab); setShowModal(true); };
  const handleEdit = (item) => { setEditingItem(item); setModalType(activeTab); setShowModal(true); };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu öğeyi silmek istediğinize emin misiniz?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = activeTab === 'messages' ? 'contact' : activeTab;
      await axios.delete(`${API}/${endpoint}/${id}`, { headers });
      toast.success('Başarıyla silindi');
      fetchData();
    } catch (error) { toast.error('Silinirken hata oluştu'); }
  };

  const handleSave = async (formData) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let dataToSend = { ...formData }; 

      if (modalType === 'products') {
        dataToSend.price = parseFloat(dataToSend.price);
        if (isNaN(dataToSend.price)) dataToSend.price = 0.0;
        if (!dataToSend.service_id && services.length > 0) dataToSend.service_id = services[0].id;
      }
      
      if (editingItem) {
        await axios.put(`${API}/${activeTab}/${editingItem.id}`, dataToSend, { headers }); toast.success('Güncellendi');
      } else {
        await axios.post(`${API}/${activeTab}`, dataToSend, { headers }); toast.success('Eklendi');
      }
      setShowModal(false);
      fetchData();
    } catch (error) { toast.error('Hata oluştu! Zorunlu alanları kontrol edin.'); }
  };

  const handleSettingsSave = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/settings`, siteSettings, { headers });
      toast.success('Ayarlar başarıyla güncellendi!');
    } catch (error) { toast.error('Ayarlar kaydedilemedi.'); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) { toast.error('Şifreler eşleşmiyor'); return; }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/auth/change-password`, { current_password: passwordForm.current_password, new_password: passwordForm.new_password }, { headers });
      toast.success('Şifre başarıyla değiştirildi');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) { toast.error('Şifre değiştirilemedi.'); }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/auth/change-email`, emailForm, { headers });
      toast.success('Sistem maili başarıyla güncellendi! Çıkış yapılıyor...');
      setEmailForm({ new_email: '', current_password: '' });
      setTimeout(() => { handleLogout(); }, 2500);
    } catch (error) { toast.error('Mail değiştirilemedi.'); }
  };

  // WEBHOOK KOPYALAMA İŞLEMİ
  const handleCopyWebhook = () => {
    navigator.clipboard.writeText("https://mertt.tr/api/webhook/payment-success");
    toast.success("Webhook URL Kopyalandı! Bunu ödeme sağlayıcınıza yapıştırın.");
  };

  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">Yükleniyor...</div>;

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100" data-testid="admin-dashboard">
      <header className="glass border-b border-cyan-900/30 sticky top-0 z-10 bg-slate-900/80 backdrop-blur">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
            MERTT <span className="text-sm text-gray-400 ml-2 border-l border-gray-600 pl-2">KOMUTA MERKEZİ</span>
          </h1>
          <button onClick={handleLogout} className="text-red-400 hover:text-red-300 flex items-center space-x-2 transition">
            <LogOut size={18} /><span>Sistemi Kilitle</span>
          </button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* SIDEBAR MENÜ */}
          <div className="lg:col-span-1">
            <div className="glass p-4 space-y-2 rounded-xl border border-slate-700">
              {[
                { id: 'settings', icon: Settings, label: 'Genel Ayarlar' },
                { id: 'services', icon: Package, label: 'Hizmetler' },
                { id: 'products', icon: ShoppingCart, label: 'Ürünler' },
                { id: 'blogs', icon: FileText, label: 'Blog Yazıları' },
                { id: 'messages', icon: Mail, label: 'Mesajlar' },
                { id: 'analytics', icon: Globe, label: 'Trafik & Analiz' }, 
                { id: 'automation', icon: Zap, label: 'Otomasyon & API' }, // YENİ EKLENDİ
                { id: 'security', icon: ShieldCheck, label: 'Güvenlik & Kasa' }, 
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    activeTab === item.id 
                    ? (item.id === 'security' ? 'bg-red-600 text-white shadow-lg shadow-red-500/30' : 
                       item.id === 'automation' ? 'bg-orange-600 text-white shadow-lg shadow-orange-500/30' : 
                       'bg-cyan-600 text-white shadow-lg shadow-cyan-500/30') 
                    : 'text-gray-400 hover:bg-slate-800'
                  }`}
                >
                  <item.icon size={20} />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* İÇERİK ALANI */}
          <div className="lg:col-span-3">
            <div className="glass p-6 rounded-xl border border-slate-700 min-h-[500px]">
              
              {/* Ekle Butonu Kontrolü */}
              {['services', 'products', 'blogs'].includes(activeTab) && (
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold text-cyan-400 uppercase tracking-wide">{activeTab}</h2>
                  <button onClick={handleAdd} className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition shadow-lg shadow-cyan-500/20">
                    <Plus size={18} /><span>Ekle</span>
                  </button>
                </div>
              )}

              {/* --- 1. SETTINGS TAB --- */}
              {activeTab === 'settings' && (
                <div className="space-y-8 animate-fade-in">
                  <h2 className="text-2xl font-semibold text-cyan-400 mb-6 flex items-center gap-2">
                    <Settings size={24}/> Site Yönetimi
                  </h2>

                  <form onSubmit={handleSettingsSave} className="space-y-6">
                    <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                      <h3 className="text-lg font-semibold text-blue-400 mb-4 flex items-center gap-2"><Phone size={18}/> İletişim</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <input type="text" placeholder="Telefon" className="input-field" value={siteSettings.phone} onChange={e=>setSiteSettings({...siteSettings, phone:e.target.value})} />
                          <input type="email" placeholder="E-Mail" className="input-field" value={siteSettings.email} onChange={e=>setSiteSettings({...siteSettings, email:e.target.value})} />
                          <textarea placeholder="Adres" rows="2" className="input-field md:col-span-2" value={siteSettings.address} onChange={e=>setSiteSettings({...siteSettings, address:e.target.value})} />
                      </div>
                    </div>

                    <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                      <h3 className="text-lg font-semibold text-pink-400 mb-4 flex items-center gap-2"><Globe size={18}/> Sosyal Medya</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <input type="text" placeholder="Facebook Link" className="input-field" value={siteSettings.facebook || ''} onChange={e=>setSiteSettings({...siteSettings, facebook:e.target.value})} />
                          <input type="text" placeholder="Instagram Link" className="input-field" value={siteSettings.instagram} onChange={e=>setSiteSettings({...siteSettings, instagram:e.target.value})} />
                          <input type="text" placeholder="LinkedIn Link" className="input-field" value={siteSettings.linkedin} onChange={e=>setSiteSettings({...siteSettings, linkedin:e.target.value})} />
                          <input type="text" placeholder="Twitter (X) Link" className="input-field" value={siteSettings.twitter} onChange={e=>setSiteSettings({...siteSettings, twitter:e.target.value})} />
                          <input type="text" placeholder="WhatsApp No" className="input-field" value={siteSettings.whatsapp} onChange={e=>setSiteSettings({...siteSettings, whatsapp:e.target.value})} />
                      </div>
                    </div>

                    <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                      <h3 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2"><Info size={18}/> Hakkımızda Sayfası</h3>
                      <div className="space-y-4">
                          <input type="text" placeholder="Hakkımızda Başlığı" className="input-field" value={siteSettings.about_title} onChange={e=>setSiteSettings({...siteSettings, about_title:e.target.value})} />
                          <textarea placeholder="Hakkımızda Açıklama" rows="3" className="input-field" value={siteSettings.about_desc} onChange={e=>setSiteSettings({...siteSettings, about_desc:e.target.value})} />
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                             <textarea placeholder="Vizyon" rows="3" className="input-field" value={siteSettings.vision} onChange={e=>setSiteSettings({...siteSettings, vision:e.target.value})} />
                             <textarea placeholder="Misyon" rows="3" className="input-field" value={siteSettings.mission} onChange={e=>setSiteSettings({...siteSettings, mission:e.target.value})} />
                             <textarea placeholder="Değerler" rows="3" className="input-field" value={siteSettings.values} onChange={e=>setSiteSettings({...siteSettings, values:e.target.value})} />
                          </div>
                      </div>
                    </div>

                    <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition shadow-lg">
                      <Save className="inline mr-2" size={18}/> AYARLARI KAYDET
                    </button>
                  </form>
                </div>
              )}

              {/* --- YENİ EKLENEN: OTOMASYON & API TAB --- */}
              {activeTab === 'automation' && (
                <div className="space-y-8 animate-fade-in">
                  <h2 className="text-2xl font-semibold text-orange-400 mb-6 flex items-center gap-2">
                    <Zap size={28}/> Otomasyon ve API Entegrasyonları
                  </h2>

                  {/* WEBHOOK BİLGİ ALANI (SADECE OKUNUR) */}
                  <div className="bg-slate-800/50 p-6 rounded-xl border border-orange-900/50 shadow-lg relative overflow-hidden">
                    <div className="absolute top-0 right-0 bg-orange-600 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl shadow-lg z-10">Webhook URL</div>
                    <h3 className="text-lg font-semibold text-white mb-2">Shopier / Ödeme Sağlayıcı Bağlantısı</h3>
                    <p className="text-sm text-gray-400 mb-4">Müşteri ödeme yaptığında sistemin otomatik mail ve SMS atabilmesi için aşağıdaki adresi kopyalayıp ödeme sağlayıcınızın (Örn: Shopier) "Webhook URL" veya "Geri Dönüş URL" kısmına yapıştırın.</p>
                    
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input type="text" readOnly value="https://mertt.tr/api/webhook/payment-success" className="input-field flex-1 font-mono text-cyan-400 bg-slate-900/80" />
                      <button onClick={handleCopyWebhook} className="bg-slate-700 hover:bg-slate-600 text-white px-6 rounded-lg font-bold transition flex items-center justify-center gap-2">
                        <Copy size={18} /> Kopyala
                      </button>
                    </div>
                  </div>

                  <form onSubmit={handleSettingsSave} className="space-y-6">
                    <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                      <h3 className="text-lg font-semibold text-blue-400 mb-4 flex items-center gap-2"><Mail size={18}/> Otomatik E-Mail Ayarları (SMTP)</h3>
                      <p className="text-xs text-gray-500 mb-4">Sistemin şifre (PIN) maillerini atabilmesi için Gmail adresi ve 'Uygulama Şifresi' giriniz.</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <input type="email" placeholder="Gmail Adresiniz (Örn: mertt@gmail.com)" className="input-field" value={siteSettings.smtp_email || ''} onChange={e=>setSiteSettings({...siteSettings, smtp_email: e.target.value})} />
                          <input type="password" placeholder="16 Haneli Uygulama Şifresi" className="input-field" value={siteSettings.smtp_password || ''} onChange={e=>setSiteSettings({...siteSettings, smtp_password: e.target.value})} />
                      </div>
                    </div>

                    <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                      <h3 className="text-lg font-semibold text-pink-400 mb-4 flex items-center gap-2"><Phone size={18}/> Otomatik SMS API Ayarları</h3>
                      <p className="text-xs text-gray-500 mb-4">Müşteriye SMS atmak için anlaştığınız firmanın (İletiMerkezi, Netgsm vb.) API Anahtarını girin.</p>
                      <div className="grid grid-cols-1 gap-4">
                          <input type="password" placeholder="SMS API Key (Secret)" className="input-field" value={siteSettings.sms_api_key || ''} onChange={e=>setSiteSettings({...siteSettings, sms_api_key: e.target.value})} />
                      </div>
                    </div>

                    <button type="submit" className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 rounded-lg transition shadow-lg shadow-orange-900/50">
                      <Save className="inline mr-2" size={18}/> OTOMASYON AYARLARINI KAYDET
                    </button>
                  </form>
                </div>
              )}

              {/* --- TRAFİK & ANALİZ TAB --- */}
              {activeTab === 'analytics' && (
                <div className="space-y-8 animate-fade-in">
                  <h2 className="text-2xl font-semibold text-cyan-400 mb-6 flex items-center gap-2">
                    <Globe size={28}/> Ziyaretçi Trafiği ve Analiz
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-slate-800/50 p-6 rounded-xl border border-cyan-900/50 shadow-lg flex flex-col justify-center items-center text-center">
                      <h3 className="text-gray-400 text-sm font-semibold mb-2 uppercase tracking-wider">Bugünkü Tekil Ziyaretçi</h3>
                      <p className="text-6xl font-bold text-white mt-2">{analyticsData.today_visitors}</p>
                    </div>
                    <div className="bg-slate-800/50 p-6 rounded-xl border border-blue-900/50 shadow-lg flex flex-col justify-center items-center text-center">
                      <h3 className="text-gray-400 text-sm font-semibold mb-2 uppercase tracking-wider">Toplam Tekil Ziyaretçi</h3>
                      <p className="text-6xl font-bold text-cyan-400 mt-2">{analyticsData.total_visitors}</p>
                    </div>
                  </div>

                  <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                    <h3 className="text-lg font-semibold text-white mb-6">En Çok Ziyaret Edilen Sayfalar (Tüm Zamanlar)</h3>
                    <div className="space-y-3">
                      {analyticsData.popular_pages.map((page, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-slate-900/50 p-4 rounded-lg border border-slate-700 hover:border-cyan-700 transition">
                          <span className="text-cyan-400 font-mono text-sm break-all">{page.path}</span>
                          <span className="bg-cyan-900/50 text-cyan-300 px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ml-4">
                            {page.views} Kez Gezildi
                          </span>
                        </div>
                      ))}
                      {analyticsData.popular_pages.length === 0 && (
                        <div className="text-center py-8">
                          <Globe size={40} className="mx-auto text-slate-600 mb-3" />
                          <p className="text-gray-500 text-sm">Henüz yeterli analitik veri toplanmadı. İnsanlar siteye girdikçe burası dolacak.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* --- SECURITY TAB --- */}
              {activeTab === 'security' && (
                <div className="space-y-8 animate-fade-in">
                  <h2 className="text-2xl font-semibold text-cyan-400 mb-6 flex items-center gap-2">
                    <ShieldCheck size={28}/> Güvenlik ve Kasa (MERTT V3.0)
                  </h2>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-slate-800/50 p-8 rounded-2xl border border-slate-700 shadow-xl">
                      <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2"><Lock size={20} className="text-blue-400"/> Sistem Şifresini Değiştir</h3>
                      <form onSubmit={handlePasswordChange} className="space-y-5">
                        <div><input type="password" placeholder="Mevcut şifreniz" className="input-field" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} required /></div>
                        <div><input type="password" placeholder="Yeni güçlü şifreniz" className="input-field" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} required /></div>
                        <div><input type="password" placeholder="Yeni şifrenizi doğrulayın" className="input-field" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} required /></div>
                        <button type="submit" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white py-3 px-4 rounded-xl w-full transition font-bold mt-2">Şifreyi Güncelle</button>
                      </form>
                    </div>

                    <div className="bg-slate-800/50 p-8 rounded-2xl border border-slate-700 shadow-xl">
                      <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2"><AtSign size={20} className="text-purple-400"/> Sistem Mailini Değiştir</h3>
                      <form onSubmit={handleEmailChange} className="space-y-5">
                        <div><input type="email" placeholder="yeniadmin@mertt.com" className="input-field" value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} required /></div>
                        <div><input type="password" placeholder="Mevcut şifreniz" className="input-field" value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} required /></div>
                        <button type="submit" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white py-3 px-4 rounded-xl w-full transition font-bold mt-2">Mail Adresini Güncelle</button>
                      </form>
                    </div>

                    {/* --- 2FA AKTİF PANEL --- */}
                    <div className="lg:col-span-2 bg-slate-800/50 p-8 rounded-2xl border border-cyan-900/50 shadow-xl relative overflow-hidden flex flex-col md:flex-row gap-8 items-center">
                      <div className="absolute top-0 right-0 bg-cyan-600 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl shadow-lg z-10">2FA AKTİF</div>
                      
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                          <ShieldCheck size={20} className="text-cyan-400"/> İki Aşamalı Doğrulama (2FA)
                        </h3>
                        <p className="text-sm text-gray-400 mb-6 leading-relaxed">Google Authenticator ile sistemini dış müdahalelere kapat. 30 saniyelik rastgele şifreler olmadan giriş yapılamaz.</p>
                        
                        {!qrCode ? (
                          <button 
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API}/auth/2fa/enable`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                setQrCode(res.data.qr_code);
                                toast.success("QR Kod oluşturuldu. Lütfen okutun.");
                              } catch (error) {
                                console.error(error);
                                toast.error(error.response?.data?.detail || "Sunucudan QR kod alınamadı.");
                              }
                            }}
                            className="bg-cyan-600 hover:bg-cyan-700 text-white py-3 px-6 rounded-xl font-bold w-full md:w-auto transition"
                          >
                            Kurulumu Başlat
                          </button>
                        ) : (
                          <div className="space-y-4">
                            <input type="text" placeholder="6 Haneli Kodu Gir" className="input-field" onChange={(e) => setVerificationCode(e.target.value)} />
                            <button 
                              onClick={async () => {
                                if (!verificationCode) {
                                  toast.error("Lütfen kodu girin."); return;
                                }
                                try {
                                  await axios.post(`${API}/auth/2fa/verify`, { code: verificationCode }, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success("Güvenlik duvarı başarıyla aktif edildi!");
                                  setQrCode(null);
                                  setVerificationCode("");
                                } catch (error) {
                                  console.error(error);
                                  toast.error(error.response?.data?.detail || "Hatalı kod girdiniz.");
                                }
                              }}
                              className="bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-xl font-bold w-full"
                            >
                              Doğrula ve Aktif Et
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="w-full md:w-1/3 flex justify-center items-center bg-black/60 rounded-xl border border-slate-700 p-2">
                        {qrCode ? (
                          <img src={`data:image/png;base64,${qrCode}`} alt="2FA QR" className="w-32 h-32" />
                        ) : (
                          <div className="text-center py-8">
                            <ShieldCheck size={48} className="text-gray-700 mx-auto mb-2"/>
                            <span className="text-gray-500 text-xs">[QR KOD BEKLENİYOR]</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* --- LİSTE GÖRÜNÜMÜ --- */}
              {(activeTab === 'services' || activeTab === 'products' || activeTab === 'blogs') && (
                <div className="space-y-4 animate-fade-in">
                  {(activeTab === 'services' ? services : activeTab === 'products' ? products : blogs).map((item) => (
                    <div key={item.id} className="bg-slate-800/40 p-4 rounded-lg border border-slate-700 hover:border-cyan-500/50 transition flex items-start justify-between group">
                      <div className="flex space-x-4">
                        <img 
                          src={getImageUrl(item.image_url)} 
                          alt="item" 
                          className="w-24 h-24 object-cover rounded-lg bg-slate-900 border border-slate-600 group-hover:scale-105 transition"
                          onError={(e) => {e.target.src = 'https://via.placeholder.com/150?text=No+Image'}}
                        />
                        <div>
                          <h3 className="text-lg font-semibold text-cyan-400 flex items-center gap-2">
                            {item.title_tr || item.name_tr} 
                            {item.file_url && <FileArchive size={14} className="text-purple-400" title="Dosya Ekli" />}
                          </h3>
                          <p className="text-sm text-gray-400 mt-1 line-clamp-2 max-w-md">
                            {item.description_tr || item.excerpt_tr}
                          </p>
                          
                          {item.seo_tags && (
                             <p className="text-xs text-blue-400 mt-1">{item.seo_tags}</p>
                          )}

                          {activeTab === 'blogs' && (
                             <span className={`text-xs px-2 py-1 rounded mt-2 inline-block ${item.published ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                               {item.published ? 'Yayında' : 'Taslak'}
                             </span>
                          )}
                          
                          {activeTab === 'products' && (
                             <span className="text-xs px-2 py-1 rounded mt-2 inline-block bg-blue-500/20 text-blue-400 font-bold">
                               {item.price} {getCurrencySymbol(item.currency) === '₺' ? '₺' : item.currency || 'TL'}
                             </span>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition">
                        <button onClick={() => handleEdit(item)} className="p-2 text-cyan-400 hover:bg-cyan-500/20 rounded-lg"><Edit size={18} /></button>
                        <button onClick={() => handleDelete(item.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  ))}
                  {(activeTab === 'services' ? services : activeTab === 'products' ? products : blogs).length === 0 && (
                    <p className="text-center text-gray-500 py-10">Henüz kayıt yok. Ekle butonunu kullanın.</p>
                  )}
                </div>
              )}

              {/* --- MESSAGES TAB --- */}
              {activeTab === 'messages' && (
                <div className="space-y-4 animate-fade-in">
                  <h2 className="text-2xl font-semibold text-cyan-400 mb-6">Gelen Mesajlar</h2>
                  {messages.map((msg) => (
                    <div key={msg.id} className="bg-slate-800/40 p-4 rounded-lg border border-slate-700 flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-cyan-400">{msg.name} <span className="text-xs text-gray-500 ml-2">{new Date(msg.created_at).toLocaleDateString()}</span></h3>
                        <p className="text-sm text-gray-400">{msg.email}</p>
                        <h4 className="font-medium text-white mt-2 border-b border-slate-700 pb-1 inline-block">{msg.subject}</h4>
                        <p className="text-sm text-gray-300 mt-2 bg-slate-900/50 p-3 rounded">{msg.message}</p>
                      </div>
                      <button onClick={() => handleDelete(msg.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"><Trash2 size={20} /></button>
                    </div>
                  ))}
                  {messages.length === 0 && <p className="text-gray-500 text-center py-8">Henüz mesaj yok.</p>}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <AdminModal
          type={modalType}
          item={editingItem}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
          services={services}
          token={token}
        />
      )}
    </div>
  );
}

// --- GELİŞMİŞ MODAL COMPONENTİ ---
function AdminModal({ type, item, onSave, onClose, services, token }) {
  const [uploading, setUploading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false); 
  
  const [formData, setFormData] = useState(() => {
    if (type === 'services') return item || { title_tr: '', title_en: '', description_tr: '', description_en: '', icon: '', image_url: '' };
    
    // ÇİFT BUTONLU ÜRÜN MODELİ
    if (type === 'products') return item || { 
        service_id: services[0]?.id || '', 
        name_tr: '', name_en: '', 
        slug: '', 
        description_tr: '', description_en: '', 
        details_tr: '', details_en: '', 
        price: 0, 
        currency: 'TL', 
        payment_link: '', 
        unlock_pin: '', 
        image_url: '',
        file_url: '', 
        // 1. BUTON (SOL)
        button_text_tr: 'İncele', 
        button_text_en: 'Details', 
        button_action_type: 'internal', 
        external_link: '', 
        // 2. BUTON (SAĞ)
        button2_text_tr: 'Satın Al', 
        button2_text_en: 'Buy', 
        button2_action_type: 'external', 
        button2_external_link: '', 
        seo_tags: '' 
    };
    if (type === 'blogs') return item || { title_tr: '', title_en: '', content_tr: '', content_en: '', excerpt_tr: '', excerpt_en: '', image_url: '', published: true };
    return {};
  });

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const data = new FormData();
    data.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, data, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setFormData({ ...formData, image_url: response.data.url });
      toast.success('Resim başarıyla yüklendi!');
    } catch (error) {
      toast.error('Resim yüklenemedi.');
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    const data = new FormData();
    data.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, data, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setFormData({ ...formData, file_url: response.data.url });
      toast.success('Dijital dosya güvenle yüklendi!');
    } catch (error) {
      toast.error('Dosya yüklenemedi.');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="glass max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 rounded-xl border border-slate-600 bg-slate-900 shadow-2xl">
        <div className="flex items-center justify-between mb-6 border-b border-slate-700 pb-4 sticky top-0 bg-slate-900 z-10">
          <h2 className="text-2xl font-semibold text-cyan-400 capitalize">
            {item ? 'Düzenle' : 'Yeni Ekle'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition bg-slate-800 p-2 rounded-lg"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          
          <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
            <label className="block text-sm font-medium text-gray-300 mb-2">Vitrin Görseli</label>
            <div className="flex items-start gap-4">
                <div className="w-24 h-24 bg-slate-900 rounded border border-slate-600 flex items-center justify-center overflow-hidden shrink-0">
                    {formData.image_url ? (
                        <img src={getImageUrl(formData.image_url)} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                        <ImageIcon className="text-gray-600" size={32} />
                    )}
                </div>
                <div className="flex-1">
                    <label className="bg-slate-700 hover:bg-slate-600 text-white cursor-pointer inline-flex items-center gap-2 text-sm py-2 px-4 rounded transition mb-2">
                        <Upload size={16} />
                        {uploading ? 'Yükleniyor...' : 'Bilgisayardan Seç'}
                        <input type="file" className="hidden" onChange={handleImageUpload} accept="image/*" />
                    </label>
                    <div className="text-xs text-gray-500 mb-1">Veya resim linki yapıştırın:</div>
                    <input type="text" className="input-field" placeholder="https://..." value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} />
                </div>
            </div>
          </div>

          {type === 'services' && (
            <div className="grid grid-cols-2 gap-4">
                <input type="text" placeholder="Başlık (TR)" className="input-field" value={formData.title_tr} onChange={(e) => setFormData({...formData, title_tr: e.target.value})} required />
                <input type="text" placeholder="Title (EN)" className="input-field" value={formData.title_en} onChange={(e) => setFormData({...formData, title_en: e.target.value})} required />
                <textarea placeholder="Açıklama (TR)" rows="3" className="input-field col-span-2" value={formData.description_tr} onChange={(e) => setFormData({...formData, description_tr: e.target.value})} required />
                <textarea placeholder="Description (EN)" rows="3" className="input-field col-span-2" value={formData.description_en} onChange={(e) => setFormData({...formData, description_en: e.target.value})} required />
                <input type="text" placeholder="İkon Adı (örn: Brain)" className="input-field col-span-2" value={formData.icon} onChange={(e) => setFormData({...formData, icon: e.target.value})} />
            </div>
          )}

          {/* --- ÜRÜN DÜZENLEME EKRANI --- */}
          {type === 'products' && (
            <div className="space-y-6">
                
                <div className="grid grid-cols-2 gap-4 bg-slate-800/20 p-4 rounded-lg border border-slate-700">
                    <div className="col-span-2">
                        <label className="text-sm text-gray-400 mb-1 block">Bağlı Olduğu Hizmet <span className="text-red-400">*</span></label>
                        <select className="input-field" value={formData.service_id} onChange={(e) => setFormData({...formData, service_id: e.target.value})}>
                            <option value="">Hizmet Seçiniz</option>
                            {services.map(s => <option key={s.id} value={s.id}>{s.title_tr}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 mb-1 block">Ürün Adı (TR) <span className="text-red-400">*</span></label>
                        <input type="text" placeholder="Ürün Adı" className="input-field" value={formData.name_tr} onChange={(e) => setFormData({...formData, name_tr: e.target.value})} required />
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 mb-1 block">Product Name (EN) <span className="text-red-400">*</span></label>
                        <input type="text" placeholder="Product Name" className="input-field" value={formData.name_en} onChange={(e) => setFormData({...formData, name_en: e.target.value})} required />
                    </div>
                    <textarea placeholder="Kısa Özet (TR)" rows="2" className="input-field col-span-2" value={formData.description_tr} onChange={(e) => setFormData({...formData, description_tr: e.target.value})} required />
                    <textarea placeholder="Short Summary (EN)" rows="2" className="input-field col-span-2" value={formData.description_en} onChange={(e) => setFormData({...formData, description_en: e.target.value})} required />
                </div>

                <div className="grid grid-cols-2 gap-4 bg-slate-800/30 p-4 rounded-lg border border-slate-700">
                    <div>
                        <label className="text-xs text-blue-400 font-semibold mb-1 block">Özel URL Uzantısı (Slug)</label>
                        <input type="text" placeholder="ornek-urun-adi" className="input-field" value={formData.slug} onChange={(e) => setFormData({...formData, slug: e.target.value})} />
                    </div>
                    <div>
                        <label className="text-xs text-purple-400 font-semibold mb-1 block">SEO Etiketleri</label>
                        <input type="text" placeholder="#yazilim #yapayzeka" className="input-field" value={formData.seo_tags} onChange={(e) => setFormData({...formData, seo_tags: e.target.value})} />
                    </div>
                </div>

                <div className="bg-slate-800/30 p-4 rounded-lg border border-slate-700">
                    <label className="text-sm font-semibold text-green-400 mb-2 flex items-center gap-2"><FileArchive size={18}/> Dijital Ürün Dosyası Ekle</label>
                    <div className="flex items-center gap-4">
                        <label className="bg-green-700 hover:bg-green-600 text-white cursor-pointer inline-flex items-center gap-2 text-sm py-2 px-4 rounded transition shrink-0">
                            <Upload size={16} />
                            {uploadingFile ? 'Yükleniyor...' : 'Dosya Seç & Yükle'}
                            <input type="file" className="hidden" onChange={handleDocumentUpload} accept=".zip,.rar,.apk,.pdf,.exe,.msi,.docx,.xlsx" />
                        </label>
                        <input type="text" className="input-field flex-1" placeholder="Yüklenen dosyanın adresi otomatik görünecek" value={formData.file_url} onChange={(e) => setFormData({...formData, file_url: e.target.value})} />
                    </div>
                </div>

                {/* ÇİFT BUTON VE KASA AYARLARI */}
                <div className="bg-slate-800/30 p-4 rounded-lg border border-slate-700 space-y-4">
                    <h3 className="text-sm font-semibold text-pink-400 flex items-center gap-2"><Link size={18}/> Fiyat, Kasa ve Çift Buton Ayarları</h3>
                    
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="text-xs text-gray-400 mb-1 block">Fiyat</label>
                            <input type="number" placeholder="0.00" className="input-field" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} required />
                        </div>
                        <div>
                            <label className="text-xs text-gray-400 mb-1 block">Para Birimi</label>
                            <select className="input-field" value={formData.currency || 'TL'} onChange={(e) => setFormData({...formData, currency: e.target.value})}>
                                <option value="TL">₺ (TL)</option>
                                <option value="USD">$ (USD)</option>
                                <option value="EUR">€ (Euro)</option>
                                <option value="BTC">₿ (Bitcoin)</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-orange-400 mb-1 block">Kasa Şifresi (PIN)</label>
                            <input type="text" placeholder="Örn: MERTT-2026" className="input-field border-orange-900/50" value={formData.unlock_pin || ''} onChange={(e) => setFormData({...formData, unlock_pin: e.target.value})} />
                        </div>
                        <div>
                            <label className="text-xs text-green-400 mb-1 block">Gizli Ödeme Linki (Kasa)</label>
                            <input type="text" placeholder="https://shopier..." className="input-field border-green-900/50" value={formData.payment_link || ''} onChange={(e) => setFormData({...formData, payment_link: e.target.value})} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-black/20 p-3 rounded-lg border border-slate-700/50 mt-4">
                        {/* SOL BUTON */}
                        <div className="space-y-3 p-3 border border-cyan-900/50 rounded-lg">
                            <h4 className="text-xs font-bold text-cyan-400 uppercase tracking-wider">1. Buton (Sol Buton)</h4>
                            <div className="flex gap-2">
                                <input type="text" placeholder="TR: İncele" className="input-field flex-1" value={formData.button_text_tr} onChange={(e) => setFormData({...formData, button_text_tr: e.target.value})} />
                                <input type="text" placeholder="EN: Details" className="input-field flex-1" value={formData.button_text_en} onChange={(e) => setFormData({...formData, button_text_en: e.target.value})} />
                            </div>
                            <select className="input-field" value={formData.button_action_type} onChange={(e) => setFormData({...formData, button_action_type: e.target.value})}>
                                <option value="internal">Ürün Detayına Git (Site İçi)</option>
                                <option value="external">Dış Linke Yönlendir (Örn: Demo)</option>
                            </select>
                            {formData.button_action_type === 'external' && (
                                <input type="text" placeholder="Yönlendirilecek Dış Link" className="input-field border-cyan-900/50" value={formData.external_link} onChange={(e) => setFormData({...formData, external_link: e.target.value})} />
                            )}
                        </div>

                        {/* SAĞ BUTON */}
                        <div className="space-y-3 p-3 border border-pink-900/50 rounded-lg">
                            <h4 className="text-xs font-bold text-pink-400 uppercase tracking-wider">2. Buton (Sağ Buton)</h4>
                            <div className="flex gap-2">
                                <input type="text" placeholder="TR: Satın Al" className="input-field flex-1" value={formData.button2_text_tr || ''} onChange={(e) => setFormData({...formData, button2_text_tr: e.target.value})} />
                                <input type="text" placeholder="EN: Buy" className="input-field flex-1" value={formData.button2_text_en || ''} onChange={(e) => setFormData({...formData, button2_text_en: e.target.value})} />
                            </div>
                            <select className="input-field" value={formData.button2_action_type || 'external'} onChange={(e) => setFormData({...formData, button2_action_type: e.target.value})}>
                                <option value="internal">Ürün Detayına Git (Site İçi)</option>
                                <option value="external">Dış Linke Yönlendir (Örn: Satın Al)</option>
                            </select>
                            {formData.button2_action_type === 'external' && (
                                <input type="text" placeholder="Yönlendirilecek Dış Link" className="input-field border-pink-900/50" value={formData.button2_external_link || ''} onChange={(e) => setFormData({...formData, button2_external_link: e.target.value})} />
                            )}
                        </div>
                    </div>
                </div>

                <div className="border border-slate-700 rounded-lg overflow-hidden">
                    <div className="bg-[#1e1e1e] text-gray-300 p-2 text-sm flex justify-between items-center border-b border-slate-700">
                        <span>Ürün Detay Sayfası Tasarımı (HTML/CSS - TR)</span>
                        <span className="text-xs text-gray-500">VS Code Mode</span>
                    </div>
                    <Editor
                        height="400px"
                        defaultLanguage="html"
                        theme="vs-dark"
                        value={formData.details_tr || ''}
                        onChange={(value) => setFormData({ ...formData, details_tr: value })}
                        options={{ minimap: { enabled: false }, fontSize: 14, wordWrap: 'on', formatOnPaste: true, automaticLayout: true }}
                    />
                </div>

                <div className="border border-slate-700 rounded-lg overflow-hidden">
                    <div className="bg-[#1e1e1e] text-gray-300 p-2 text-sm flex justify-between items-center border-b border-slate-700">
                         <span>Product Detail Page Design (HTML/CSS - EN)</span>
                         <span className="text-xs text-gray-500">VS Code Mode</span>
                    </div>
                     <Editor
                        height="400px"
                        defaultLanguage="html"
                        theme="vs-dark"
                        value={formData.details_en || ''}
                        onChange={(value) => setFormData({ ...formData, details_en: value })}
                        options={{ minimap: { enabled: false }, fontSize: 14, wordWrap: 'on', formatOnPaste: true, automaticLayout: true }}
                    />
                </div>
            </div>
          )}

          {type === 'blogs' && (
            <div className="grid grid-cols-2 gap-4">
                <input type="text" placeholder="Başlık (TR)" className="input-field col-span-2" value={formData.title_tr} onChange={(e) => setFormData({...formData, title_tr: e.target.value})} required />
                <input type="text" placeholder="Title (EN)" className="input-field col-span-2" value={formData.title_en} onChange={(e) => setFormData({...formData, title_en: e.target.value})} required />
                <textarea placeholder="Kısa Özet (TR)" className="input-field col-span-2" value={formData.excerpt_tr} onChange={(e) => setFormData({...formData, excerpt_tr: e.target.value})} required />
                <textarea placeholder="Short Excerpt (EN)" className="input-field col-span-2" value={formData.excerpt_en} onChange={(e) => setFormData({...formData, excerpt_en: e.target.value})} required />
                <textarea placeholder="İçerik (TR)" rows="5" className="input-field col-span-2" value={formData.content_tr} onChange={(e) => setFormData({...formData, content_tr: e.target.value})} required />
                <textarea placeholder="Content (EN)" rows="5" className="input-field col-span-2" value={formData.content_en} onChange={(e) => setFormData({...formData, content_en: e.target.value})} required />
                <div className="flex items-center gap-2 col-span-2 bg-slate-800 p-3 rounded">
                    <input type="checkbox" id="pub" className="w-5 h-5" checked={formData.published} onChange={(e) => setFormData({...formData, published: e.target.checked})} />
                    <label htmlFor="pub" className="text-white cursor-pointer select-none">Yazıyı Yayınla</label>
                </div>
            </div>
          )}

          <div className="flex space-x-4 pt-4 border-t border-slate-700 mt-4 sticky bottom-0 bg-slate-900 pb-4">
            <button type="submit" disabled={uploading || uploadingFile} className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 rounded-lg flex-1 transition shadow-lg shadow-cyan-500/30">
              {(uploading || uploadingFile) ? 'Dosya Yükleniyor Bekleyin...' : (item ? 'Güncelle' : 'Kaydet')}
            </button>
            <button type="button" onClick={onClose} className="bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg flex-1 transition">
              İptal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminDashboard;